/**
 * File: AdrExportView.tsx
 * Description: Read-only ADR table with download buttons, shown inside the
 *   Bizzdesign Integration section. Mirrors the My ADRs table from the ADR
 *   management screen but disables editing and centralises the exports here.
 * Author: System
 * Created: 2026-06-14
 * Last Modified: 2026-06-14
 */

import { ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';

import { adrService } from '../../services/adrService';
import { ADRDefinition, ADRDefinitionStatus } from '../../types/adr';
import {
  ExportRow,
  exportRowsAsCsv,
  exportRowsAsJson,
  exportRowsAsXlsx,
} from '../../utils/tableExport';
import {
  formatADRReference,
  parseADRSections,
} from '../ADRManagement/ADRDefinitionPageHelpers';

// Column headers for the exported ADR table (must match the on-screen table).
const ADR_EXPORT_HEADERS = [
  'Ref Nr.',
  'Title',
  'Status',
  'Context',
  'Decision',
  'Consequence',
  'Step-Out',
  'Approval date',
  'Approved by',
  'Related_APMID',
] as const;
const ADR_EXPORT_BASENAME = 'adrs';
const ADR_SHEET_NAME = 'ADRs';

// Parsed Context/Decision/Consequence sections keyed by ADR id.
type AdrSections = { context: string; decision: string; consequence: string };
const EMPTY_SECTIONS: AdrSections = {
  context: '',
  decision: '',
  consequence: '',
};

const EXPORT_BUTTON_CLASS =
  'inline-flex items-center justify-center gap-2 rounded-md border ' +
  'border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 ' +
  'transition-colors hover:bg-gray-100 disabled:cursor-not-allowed ' +
  'disabled:opacity-50';

interface AdrExportViewProps {
  /** The numeric project id whose ADRs should be displayed and exported. */
  projectId: number;
}

/**
 * Render the read-only ADR table and the XLSX/CSV/JSON download buttons.
 * Fetches the ADR definitions and their generated Context/Decision/Consequence
 * content for the given project.
 * @param props - Component props containing the project id.
 * @returns The ADR export view element.
 */
export const AdrExportView: React.FC<AdrExportViewProps> = ({ projectId }) => {
  const [definitions, setDefinitions] = useState<ADRDefinition[]>([]);
  const [sectionsMap, setSectionsMap] = useState<Record<string, AdrSections>>(
    {},
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Load definitions and their generated sections for the project.
  useEffect(() => {
    let cancelled = false;

    const loadAdrs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const defs = await adrService.getADRDefinitions(projectId);
        if (cancelled) {
          return;
        }
        setDefinitions(defs);

        const entries = await Promise.all(
          defs.map(async (def) => {
            if (def.status === ADRDefinitionStatus.DEFINED) {
              return [def.id, { ...EMPTY_SECTIONS }] as const;
            }
            try {
              const content = await adrService.getADRGeneratedContent(
                projectId,
                def.id,
              );
              return [def.id, parseADRSections(content)] as const;
            } catch {
              return [def.id, { ...EMPTY_SECTIONS }] as const;
            }
          }),
        );
        if (!cancelled) {
          setSectionsMap(Object.fromEntries(entries));
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load ADRs for this project.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadAdrs();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Build the ADR table rows for export, mirroring the on-screen columns.
  const buildExportRows = useCallback((): ExportRow[] => {
    return definitions.map((def, index) => {
      const sections = sectionsMap[def.id] ?? EMPTY_SECTIONS;
      return [
        formatADRReference(index + 1),
        def.title,
        def.status,
        sections.context,
        sections.decision,
        sections.consequence,
        def.stepOut ?? '',
        def.approvalDate ?? '',
        def.approvedBy ?? '',
        def.relatedApmid ?? '',
      ];
    });
  }, [definitions, sectionsMap]);

  // Export the ADR table in the requested format (xlsx, csv or json).
  const handleExport = useCallback(
    (format: 'xlsx' | 'csv' | 'json') => {
      const rows = buildExportRows();
      if (format === 'csv') {
        exportRowsAsCsv(`${ADR_EXPORT_BASENAME}.csv`, ADR_EXPORT_HEADERS, rows);
        return;
      }
      if (format === 'json') {
        const objects = rows.map((row) =>
          Object.fromEntries(
            ADR_EXPORT_HEADERS.map((header, i) => [header, row[i]]),
          ),
        );
        exportRowsAsJson(`${ADR_EXPORT_BASENAME}.json`, objects);
        return;
      }
      exportRowsAsXlsx(
        `${ADR_EXPORT_BASENAME}.xlsx`,
        ADR_EXPORT_HEADERS,
        rows,
        ADR_SHEET_NAME,
      );
    },
    [buildExportRows],
  );

  const headerCellClass =
    'px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase ' +
    'tracking-wide border-b border-gray-200';
  const bodyCellClass =
    'px-3 py-2 text-sm text-gray-800 align-top border-b border-gray-100';

  const renderTextCell = (value: string) => (
    <div className="max-h-24 overflow-y-auto whitespace-pre-wrap min-w-[12rem]">
      {value || <span className="text-gray-400">—</span>}
    </div>
  );

  const hasDefinitions = definitions.length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">ADR View</h2>
          <p className="text-sm text-gray-600 mt-1">
            Read-only view of the project ADRs. Download them here to import
            into BiZZdesign Enterprise Studio.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className={EXPORT_BUTTON_CLASS}
            onClick={() => handleExport('xlsx')}
            disabled={!hasDefinitions}
            title="Download ADRs as Excel (.xlsx)"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            XLSX
          </button>
          <button
            type="button"
            className={EXPORT_BUTTON_CLASS}
            onClick={() => handleExport('csv')}
            disabled={!hasDefinitions}
            title="Download ADRs as CSV"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            CSV
          </button>
          <button
            type="button"
            className={EXPORT_BUTTON_CLASS}
            onClick={() => handleExport('json')}
            disabled={!hasDefinitions}
            title="Download ADRs as JSON"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            JSON
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0891b2] mx-auto" />
          <p className="mt-3 text-gray-600">Loading ADRs...</p>
        </div>
      ) : !hasDefinitions ? (
        <div className="text-center py-10">
          <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">
            No ADRs yet. Define and generate ADRs in the ADRs stage first.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {ADR_EXPORT_HEADERS.map((header) => (
                  <th key={header} className={headerCellClass}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white">
              {definitions.map((def, index) => {
                const sections = sectionsMap[def.id] ?? EMPTY_SECTIONS;
                return (
                  <tr key={def.id} className="hover:bg-gray-50">
                    <td className={`${bodyCellClass} font-mono whitespace-nowrap`}>
                      {formatADRReference(index + 1)}
                    </td>
                    <td className={`${bodyCellClass} font-medium`}>
                      {def.title}
                    </td>
                    <td className={`${bodyCellClass} whitespace-nowrap capitalize`}>
                      {def.status}
                    </td>
                    <td className={bodyCellClass}>
                      {renderTextCell(sections.context)}
                    </td>
                    <td className={bodyCellClass}>
                      {renderTextCell(sections.decision)}
                    </td>
                    <td className={bodyCellClass}>
                      {renderTextCell(sections.consequence)}
                    </td>
                    <td className={bodyCellClass}>
                      {renderTextCell(def.stepOut ?? '')}
                    </td>
                    <td className={`${bodyCellClass} whitespace-nowrap`}>
                      {renderTextCell(def.approvalDate ?? '')}
                    </td>
                    <td className={`${bodyCellClass} whitespace-nowrap`}>
                      {renderTextCell(def.approvedBy ?? '')}
                    </td>
                    <td className={`${bodyCellClass} whitespace-nowrap font-mono`}>
                      {renderTextCell(def.relatedApmid ?? '')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
